import React, { useEffect } from 'react'

import { useState, useMemo } from 'react'
import { Skeleton } from '../ui/skeleton'
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  MoreHorizontal,
  Search,
  Database,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Badge } from '../ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { CircularIcon, FilterLoader, FilterSkeleton } from '../fragments'
import { UserPlusSVGIcon } from '@/assets/svg'
import type { Dictionary } from '@/types'

export interface TableColumn<T = any> {
  key: string
  header: string
  accessor: keyof T | ((item: T) => any)
  sortable?: boolean
  width?: string
  render?: (value: any, item: T) => React.ReactNode
}

export interface TableAction<T = any> {
  label: string
  onClick: (item: T) => void
  variant?: 'default' | 'destructive'
  disabled?: (item: T) => boolean
}

export interface FilterOption {
  label: string
  value: string
}

export interface ReusableTableProps<T = any> {
  title?: string
  titleIcon?: React.ReactNode
  data: T[]
  columns: TableColumn<T>[]
  actions?: TableAction<T>[]
  rowActionsMenuLocked?: (item: T) => boolean
  loading?: boolean

  // Search & Filter
  searchable?: boolean
  searchPlaceholder?: string
  filters?: {
    key: string
    label: string
    options: FilterOption[]
    loading?: boolean
  }[]

  // Pagination
  pagination?: boolean
  pageSize?: number
  currentPage?: number
  totalPages?: number
  totalItems?: number
  onPageChange?: (page: number) => void
  externalPagination?: boolean
  handleFilterItem?: (arg: Dictionary) => void
  defaultFilterValues?: Record<string, string>

  // Styling
  height?: string
  className?: string

  // Empty state
  emptyStateTitle?: string
  emptyStateDescription?: string
  emptyStateIcon?: React.ReactNode
  hasActionsButton?: boolean
  addButtonLabel?: string
  addButtonIcon?: React.ReactNode | string
  addButtonLoading?: boolean
  handleAdd?: () => void
  handleSearchItem?: (value: string) => void
  searchClassName?: string
}

type SortDirection = 'asc' | 'desc' | null

export function ReusableTable<T extends Record<string, any>>({
  title,
  titleIcon,
  data,
  columns,
  actions = [],
  rowActionsMenuLocked,
  searchable = true,
  searchPlaceholder = 'Search...',
  searchClassName,
  filters = [],
  loading,
  pagination = true,
  pageSize = 20,
  currentPage: externalCurrentPage,
  totalPages: externalTotalPages,
  totalItems: externalTotalItems,
  onPageChange,
  externalPagination = false,
  height = '600px',
  className,
  emptyStateTitle = 'No data available',
  emptyStateDescription = 'There are no items to display at the moment.',
  emptyStateIcon = <Database className='h-12 w-12 text-muted-foreground' />,
  hasActionsButton = false,
  addButtonLabel = 'Add',
  addButtonLoading = false,
  handleAdd,
  addButtonIcon,
  handleSearchItem,
  handleFilterItem,
  defaultFilterValues,
}: ReusableTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [filterValues, setFilterValues] = useState<Record<string, string>>(
    () => ({ ...(defaultFilterValues ?? {}) }),
  )

  // Get value from item using accessor
  const getValue = (item: T, accessor: keyof T | ((item: T) => any)) => {
    if (typeof accessor === 'function') {
      return accessor(item)
    }
    return item[accessor]
  }

  // Filter and search data
  const filteredData = useMemo(() => {
    let filtered = [...data]
    if (externalPagination) {
      return filtered
    }

    // Apply search
    // if (searchable && searchQuery) {
    //   filtered = filtered.filter((item) =>
    //     columns.some((column) => {
    //       const value = getValue(item, column.accessor)
    //       return String(value).toLowerCase().includes(searchQuery.toLowerCase())
    //     })
    //   )
    // }

    // Apply filters
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value !== 'all') {
        filtered = filtered.filter((item) => {
          const itemValue = getValue(item, key as keyof T)
          return String(itemValue).toLowerCase() === value.toLowerCase()
        })
      }
    })

    return filtered
  }, [data, searchQuery, filterValues, columns, externalPagination])

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) return filteredData

    return [...filteredData].sort((a, b) => {
      const column = columns.find((col) => col.key === sortColumn)
      if (!column) return 0

      const aValue = getValue(a, column.accessor)
      const bValue = getValue(b, column.accessor)

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredData, sortColumn, sortDirection, columns])

  // Use external pagination values if provided
  const currentPageValue = externalPagination
    ? externalCurrentPage || 1
    : currentPage
  const totalPagesValue = externalPagination
    ? externalTotalPages || 1
    : Math.ceil(sortedData.length / pageSize)
  const totalItemsValue = externalPagination
    ? externalTotalItems || 0
    : sortedData.length

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData

    // For external pagination, data is already paginated by the server
    if (externalPagination) return sortedData

    const startIndex = (currentPage - 1) * pageSize
    return sortedData.slice(startIndex, startIndex + pageSize)
  }, [sortedData, currentPage, pageSize, pagination, externalPagination])

  // Handle sorting
  const handleSort = (columnKey: string) => {
    const column = columns.find((col) => col.key === columnKey)
    if (!column?.sortable) return

    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortColumn(null)
        setSortDirection(null)
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  // Get sort icon
  const getSortIcon = (columnKey: string) => {
    const column = columns.find((col) => col.key === columnKey)
    if (!column?.sortable) return null

    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        return <ChevronUp className='h-4 w-4' />
      } else if (sortDirection === 'desc') {
        return <ChevronDown className='h-4 w-4' />
      }
    }
    return <ChevronsUpDown className='h-4 w-4 opacity-50' />
  }

  // Render cell content
  const renderCell = (item: T, column: TableColumn<T>) => {
    const value = getValue(item, column.accessor)

    if (column.render) {
      return column.render(value, item)
    }

    // Default rendering for common types
    if (typeof value === 'boolean') {
      return (
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? 'Active' : 'Inactive'}
        </Badge>
      )
    }

    if (React.isValidElement(value)) {
      return value
    }

    return String(value)
  }

  useEffect(() => {
    if (handleSearchItem) {
      handleSearchItem(searchQuery)
    }
  }, [searchQuery])

  return (
    <div
      className={cn(
        'flex flex-col h-[40rem] overflow-y-auto p-3 custom-scroll',
        className,
      )}
    >
      {/* Header with search and filters */}
      {(searchable || filters.length > 0 || hasActionsButton) && (
        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4'>
          <div className='flex items-center gap-2'>
            <CircularIcon icon={titleIcon} />{' '}
            <h1 className='SFProMedium tracking-wide text-xl text-[#424242]'>
              {title}
            </h1>
          </div>
          <div className='flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto'>
            {searchable && (
              <div
                className={cn(
                  'relative flex-1 w-full sm:w-auto sm:min-w-[200px] md:min-w-[280px]',
                  searchClassName,
                )}
              >
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                <Input
                  id='search'
                  name='search'
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    handleSearchItem && handleSearchItem(e.target.value)

                    // Reset to page 1 when search changes
                    if (externalPagination && onPageChange) {
                      onPageChange(1)
                    } else {
                      setCurrentPage(1)
                    }
                  }}
                  className='pl-9 rounded-lg h-9 focus-visible::ring-transparent focus:border-none w-full'
                />
              </div>
            )}
            <div className='flex flex-col sm:flex-row gap-2 w-full sm:w-auto'>
              {filters.map((filter) => (
                <Select
                  key={filter.key}
                  value={filterValues[filter.key] || 'all'}
                  onValueChange={(value) => {
                    const newFilterValues = {
                      ...filterValues,
                      [filter.key]: value,
                    }
                    setFilterValues(newFilterValues)
                    handleFilterItem && handleFilterItem(newFilterValues)

                    // Reset to page 1 when filter changes
                    if (externalPagination && onPageChange) {
                      onPageChange(1)
                    } else {
                      setCurrentPage(1)
                    }
                  }}
                  disabled={filter.loading}
                >
                  <SelectTrigger className='w-full sm:w-[180px]'>
                    {filter.loading ? (
                      <FilterLoader size='sm' showText={false} />
                    ) : (
                      <SelectValue placeholder={filter.label} />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All {filter.label}</SelectItem>
                    {filter.loading ? (
                      <>
                        <SelectItem value='loading' disabled>
                          <FilterLoader size='sm' text='Loading options...' />
                        </SelectItem>
                        {/* Show skeleton options while loading */}
                        {Array.from({ length: 3 }).map((_, index) => (
                          <SelectItem
                            key={`skeleton-${index}`}
                            value={`skeleton-${index}`}
                            disabled
                          >
                            <FilterSkeleton />
                          </SelectItem>
                        ))}
                      </>
                    ) : (
                      filter.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              ))}
            </div>
            {hasActionsButton && (
              <Button
                variant='default'
                size='sm'
                className='w-full sm:w-fit gap-1 text-[14px]'
                onClick={() => handleAdd?.()}
                disabled={addButtonLoading}
                loading={addButtonLoading}
              >
                {addButtonIcon || UserPlusSVGIcon}
                {addButtonLabel}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Table container with fixed height */}
      <div className='flex-1 overflow-hidden' style={{ height }}>
        <div className='h-full flex flex-col'>
          <div className='flex-1 overflow-auto'>
            <Table className='border-0'>
              <TableHeader className='sticky top-0 bg-[#F6F8FA] border-none rounded-[.4rem]'>
                <TableRow className='!border-0'>
                  {columns.map((column) => (
                    <TableHead
                      key={column.key}
                      className={cn(
                        'border-none',
                        column.sortable &&
                          'cursor-pointer hover:text-foreground select-none',
                      )}
                      style={{ width: column.width }}
                      onClick={() => handleSort(column.key)}
                    >
                      <div className='flex items-center gap-2'>
                        {column.header}
                        {getSortIcon(column.key)}
                      </div>
                    </TableHead>
                  ))}
                  {actions.length > 0 && (
                    <TableHead className='w-16'>Action</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, rowIdx) => (
                    <TableRow key={rowIdx} className='border-none'>
                      {columns.map((_, colIdx) => (
                        <TableCell
                          key={colIdx}
                          className='py-3.5 border-[#E2E4E9]'
                        >
                          <Skeleton className='h-4 w-full bg-gray-100' />
                        </TableCell>
                      ))}
                      {actions.length > 0 && (
                        <TableCell>
                          <Skeleton className='h-8 w-8' />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
                      className='p-0'
                    >
                      <div
                        className='flex flex-col items-center justify-center p-8 text-center'
                        style={{ minHeight: '500px' }}
                      >
                        {emptyStateIcon}
                        <h3 className='mt-4 text-lg font-semibold'>
                          {emptyStateTitle}
                        </h3>
                        <p className='mt-2 text-sm text-muted-foreground max-w-sm'>
                          {emptyStateDescription}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow key={index} className='cursor-pointer'>
                      {columns.map((column) => (
                        <TableCell
                          key={column.key}
                          className='py-3.5 border-[#E2E4E9] text-[13px]'
                        >
                          {renderCell(item, column)}
                        </TableCell>
                      ))}
                      {actions.length > 0 && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-8 w-8 p-0'
                                disabled={rowActionsMenuLocked?.(item) ?? false}
                              >
                                <MoreHorizontal className='h-4 w-4' />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                              {actions.map((action, actionIndex) => {
                                const isDisabled = action.disabled
                                  ? action.disabled(item)
                                  : false
                                return (
                                  <DropdownMenuItem
                                    key={actionIndex}
                                    onClick={() =>
                                      !isDisabled && action.onClick(item)
                                    }
                                    disabled={isDisabled}
                                    className={cn(
                                      action.variant === 'destructive' &&
                                        'text-destructive focus:text-destructive',
                                      isDisabled &&
                                        'opacity-50 cursor-not-allowed',
                                    )}
                                  >
                                    {action.label}
                                  </DropdownMenuItem>
                                )
                              })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {pagination && sortedData.length > 0 && (
        <div className='flex items-center justify-between p-4'>
          <div className='text-sm text-gray-400'>
            Showing data {(currentPageValue - 1) * pageSize + 1} to{' '}
            {Math.min(currentPageValue * pageSize, totalItemsValue)} of{' '}
            {totalItemsValue.toLocaleString()} entries
          </div>

          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                if (externalPagination && onPageChange) {
                  onPageChange(Math.max(1, currentPageValue - 1))
                } else {
                  setCurrentPage((prev) => Math.max(1, prev - 1))
                }
              }}
              disabled={currentPageValue === 1}
              className='shadow-none'
            >
              <ChevronLeft className='text-gray-400' size={16} />
            </Button>

            {Array.from({ length: Math.min(5, totalPagesValue) }, (_, i) => {
              const pageNum = i + 1
              return (
                <Button
                  key={pageNum}
                  variant={currentPageValue === pageNum ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => {
                    if (externalPagination && onPageChange) {
                      onPageChange(pageNum)
                    } else {
                      setCurrentPage(pageNum)
                    }
                  }}
                  className='w-8 shadow-none'
                >
                  {pageNum}
                </Button>
              )
            })}

            {totalPagesValue > 5 && (
              <>
                <span className='text-sm text-muted-foreground'>...</span>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    if (externalPagination && onPageChange) {
                      onPageChange(totalPagesValue)
                    } else {
                      setCurrentPage(totalPagesValue)
                    }
                  }}
                  className='w-8'
                >
                  {totalPagesValue}
                </Button>
              </>
            )}

            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                if (externalPagination && onPageChange) {
                  onPageChange(Math.min(totalPagesValue, currentPageValue + 1))
                } else {
                  setCurrentPage((prev) => Math.min(totalPagesValue, prev + 1))
                }
              }}
              disabled={currentPageValue === totalPagesValue}
              className='shadow-none'
            >
              <ChevronRight className='text-gray-400' size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
